'use client'

import { useRef, useState } from 'react'
import { motion, HTMLMotionProps } from 'framer-motion'
import { LinkItem } from '../config/links'
import { Badge } from './Badge'
import { getIconComponent } from '../utils/icons'

interface LinkCardProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
  link: LinkItem
  index: number
}

export function LinkCard({ link, index, className = '', ...props }: LinkCardProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [isPressed, setIsPressed] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  const magneticRef = useRef<HTMLButtonElement>(null)

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left - rect.width / 2
    const y = e.clientY - rect.top - rect.height / 2
    magneticRef.current?.animate(
      { transform: `translate(${x * 0.15}px, ${y * 0.15}px)` },
      { duration: 150, fill: 'forwards', easing: 'ease-out' }
    )
  }

  const handleMouseLeave = () => {
    magneticRef.current?.animate(
      { transform: 'translate(0, 0)' },
      { duration: 300, fill: 'forwards', easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }
    )
  }

  const buildUrl = (baseUrl: string, tracking?: Record<string, string>) => {
    if (!tracking) return baseUrl
    const params = new URLSearchParams(tracking)
    const separator = baseUrl.includes('?') ? '&' : '?'
    return `${baseUrl}${separator}${params.toString()}`
  }

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const url = buildUrl(link.url, link.tracking)
    if (link.external) {
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      window.location.href = url
    }
  }

  const Icon = getIconComponent(link.icon)

  return (
    <motion.button
      ref={(el) => { ref.current = el; magneticRef.current = el }}
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94],
        delay: index * 0.06,
      }}
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.97 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); handleMouseLeave() }}
      onMouseMove={handleMouseMove}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onClick={handleClick}
      className={`
        relative group touch-target glass rounded-2xl p-4 flex flex-col items-center gap-3
        overflow-hidden transition-all duration-200 ease-out
        ${isHovered ? 'shadow-[var(--shadow-glow)] border-accent/30' : ''}
        ${className}
      `}
      style={{
        '--card-accent': 'var(--theme-accent)',
      } as React.CSSProperties}
      {...props}
    >
      <div className="relative z-10 flex flex-col items-center gap-2 w-full">
        <motion.div
          animate={{ scale: isHovered ? 1.1 : 1, rotate: isHovered ? 3 : 0 }}
          transition={{ duration: 0.3, ease: [0.68, -0.55, 0.265, 1.55] }}
          className="text-4xl"
        >
          <Icon />
        </motion.div>

        <motion.span
          className="text-sm font-semibold text-center text-white leading-snug text-balance"
          style={{ minHeight: '1.25rem' }}
        >
          {link.label}
        </motion.span>

        {link.badge && (
          <Badge variant={link.badge} label={link.badgeLabel} />
        )}
      </div>

      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-transparent"
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      />

      <motion.div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 rounded-full"
        style={{ background: 'var(--theme-accent)' }}
        initial={{ width: 0 }}
        animate={{ width: isHovered ? '60%' : 0 }}
        transition={{ duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
      />

      <motion.div
        className="absolute inset-0"
        animate={{ opacity: isPressed ? 0.1 : 0 }}
        transition={{ duration: 0.1 }}
        style={{ background: 'white' }}
      />
    </motion.button>
  )
}